<script setup lang="ts">
// One non-blocking toast card. The lib owns the LOOK; the app owns the queue
// (a per-app useToast composable). No state, no timers here.
import LumiIcon from '../icons/LumiIcon.vue';
withDefaults(
  defineProps<{
    variant?: 'error' | 'warning' | 'info' | 'success';
    title?: string;
    dismissible?: boolean;
    dismissLabel?: string;
  }>(),
  { variant: 'info', dismissible: true, dismissLabel: 'Schließen' },
);

defineEmits<{ dismiss: [] }>();
</script>

<template>
  <!-- `data-test` so a test can tell the two notice channels apart (issue 195).
       `role="status"` cannot: the app chrome has other polite live regions, and the
       blocking dialog channel has no role of its own that a toast query would miss. -->
  <div
    class="lumi-toast"
    :class="`lumi-toast--${variant}`"
    role="status"
    aria-live="polite"
    data-test="notice-toast"
  >
    <span class="lumi-toast__icon" aria-hidden="true">
      <LumiIcon v-if="variant === 'error'" name="error" width="18" height="18" stroke-width="2" />
      <LumiIcon v-else-if="variant === 'warning'" name="warning" width="18" height="18" stroke-width="2" />
      <LumiIcon v-else-if="variant === 'success'" name="done" width="18" height="18" stroke-width="2" />
      <LumiIcon v-else name="info" width="18" height="18" stroke-width="2" />
    </span>
    <div class="lumi-toast__content">
      <p v-if="title" class="lumi-toast__title">{{ title }}</p>
      <div class="lumi-toast__message"><slot /></div>
      <div v-if="$slots.action" class="lumi-toast__action"><slot name="action" /></div>
    </div>
    <button
      v-if="dismissible"
      type="button"
      class="lumi-toast__close"
      :aria-label="dismissLabel"
      @click="$emit('dismiss')"
    >
      <LumiIcon name="remove" width="16" height="16" stroke-width="2" />
    </button>
  </div>
</template>

<style>
/* Spec §8: functional soft tint with the matching text, no stripe; floats with
   the large shadow so it reads above whatever it landed on. */
.lumi-toast{display:flex;gap:var(--space-3);align-items:flex-start;width:360px;max-width:calc(100vw - var(--space-6));border-radius:var(--radius-lg);box-shadow:var(--shadow-lg);padding:var(--space-4);font-family:var(--font-body);}
.lumi-toast__icon{flex:none;display:flex;padding-top:1px;}
.lumi-toast--error{background:var(--danger-soft);color:var(--danger-text);}
.lumi-toast--warning{background:var(--warning-soft);color:var(--warning-text);}
.lumi-toast--info{background:var(--info-soft);color:var(--info-text);}
.lumi-toast--success{background:var(--success-soft);color:var(--success-text);}
.lumi-toast__content{min-width:0;flex:1;}
.lumi-toast__title{font-size:var(--text-sm);font-weight:var(--weight-semibold);margin:0 0 2px;}
.lumi-toast__message{font-size:var(--text-sm);line-height:1.45;word-wrap:break-word;}
.lumi-toast__action{margin-top:var(--space-2);}
.lumi-toast__close{flex:none;border:none;background:none;cursor:pointer;color:inherit;opacity:.7;padding:2px;border-radius:var(--radius-sm);display:flex;}
.lumi-toast__close:hover{opacity:1;}
</style>
